// /src/utils/onboardingGateLog.js
// Dev diagnostics for identity + onboarding gates (browser console).

import {
  isIdentityCheckComplete,
  isOnboardingComplete,
  isOnboardingCompleteFromServer,
  superAdminOnboardingBypassActive,
} from './onboardingStorage';

const PREFIX = '[onboarding-gate]';

export function buildIdentityMandatoryCheck(identity) {
  const email = identity?.email;
  const discordName = identity?.discordName;
  const handles = identity?.handles;
  const gcuAccepted = identity?.gcuAccepted;
  return {
    email: { ok: Boolean(email && String(email).trim()), value: email ?? null },
    discordName: { ok: Boolean(discordName && String(discordName).trim()), value: discordName ?? null },
    handles: {
      ok: Boolean(handles?.length > 0),
      count: Array.isArray(handles) ? handles.length : 0,
    },
    gcuAccepted: { ok: gcuAccepted === true, value: gcuAccepted },
    allMandatoryMet: isIdentityCheckComplete({ email, discordName, handles, gcuAccepted }),
  };
}

export function buildOnboardingMandatoryCheck({
  onboardingCompletedAt,
  subjectOnboardingCompletedAt,
  localStorageComplete,
  isViewAsActive,
}) {
  const viewerServerComplete = isOnboardingCompleteFromServer(onboardingCompletedAt);
  const subjectServerComplete = isOnboardingCompleteFromServer(subjectOnboardingCompletedAt);
  const localComplete = localStorageComplete ?? isOnboardingComplete();
  const effectiveComplete =
    (isViewAsActive && subjectServerComplete)
    || viewerServerComplete
    || localComplete;

  return {
    viewerServerOnboardingCompletedAt: onboardingCompletedAt ?? null,
    viewerServerComplete,
    subjectOnboardingCompletedAt: subjectOnboardingCompletedAt ?? null,
    subjectServerComplete,
    localStorageMembersOnboardingCompleted: localComplete,
    isViewAsActive: Boolean(isViewAsActive),
    allMandatoryMet: effectiveComplete,
  };
}

export function logOnboardingGate(label, payload) {
  console.groupCollapsed(`${PREFIX} ${label}`);
  console.log('identity (all mandatory for site access):', payload.identity);
  console.log('onboarding (any one unlocks after identity):', payload.onboarding);
  if (payload.navigation) console.log('navigation decision:', payload.navigation);
  if (payload.context) console.log('context:', payload.context);
  console.groupEnd();
}

export function logOnboardingGateDecision({
  label,
  identity,
  onboardingCompletedAt,
  subjectOnboardingCompletedAt,
  isViewAsActive,
  isSuperAdmin,
  viewAsUserId,
  userId,
  pathname,
  statusLoading,
  securityLoading,
}) {
  const identityCheck = buildIdentityMandatoryCheck(identity);
  const onboardingCheck = buildOnboardingMandatoryCheck({
    onboardingCompletedAt,
    subjectOnboardingCompletedAt,
    isViewAsActive,
  });
  const superAdminBypass = superAdminOnboardingBypassActive(isSuperAdmin);

  const needsOnboardingRedirect =
    identityCheck.allMandatoryMet
    && !onboardingCheck.allMandatoryMet
    && !superAdminBypass;

  logOnboardingGate(label, {
    identity: identityCheck,
    onboarding: onboardingCheck,
    navigation: {
      identityComplete: identityCheck.allMandatoryMet,
      onboardingComplete: onboardingCheck.allMandatoryMet,
      superAdminBypass,
      needsOnboardingRedirect,
      wouldRedirectToOnboarding: needsOnboardingRedirect,
      wouldRedirectToIdentityCheck: !identityCheck.allMandatoryMet,
    },
    context: {
      userId: userId ?? null,
      viewAsUserId: viewAsUserId ?? null,
      isViewAsActive: Boolean(isViewAsActive),
      isSuperAdmin: Boolean(isSuperAdmin),
      pathname: pathname ?? null,
      statusLoading: Boolean(statusLoading),
      securityLoading: Boolean(securityLoading),
    },
  });
}
